import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Heart, ShoppingBag, ArrowsClockwise, Crown, MagnifyingGlassPlus, X, SealCheck, Lightning, PawPrint } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { WigglezMark } from "@/components/WigglezMark";

const COLORS = [
  { name: "Lime", hex: "#ccff00" },
  { name: "Hot Pink", hex: "#ff1493" },
  { name: "Aqua", hex: "#00e5e5" },
  { name: "Purple", hex: "#b026ff" },
  { name: "Magenta", hex: "#ff00c8" },
  { name: "Orange", hex: "#ff5e00" },
];

const COLORWAY_LABEL = {
  "Lime": "LIME ASSASSIN", "Hot Pink": "PINK VENOM", "Aqua": "AQUA ICE",
  "Purple": "PURPLE REIGN", "Magenta": "MAGENTA RIOT", "Orange": "ORANGE CRUSH",
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [saved, setSaved] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [sub, setSub] = useState(false);
  const [isVIP, setIsVIP] = useState(false);
  const [color, setColor] = useState(null);
  const [colorImg, setColorImg] = useState(null);
  const [tinted, setTinted] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [pack, setPack] = useState([]);

  useEffect(() => {
    api.get(`/product/${id}`).catch(() => api.get(`/products/${id}`)).then(({ data }) => setProduct(data)).catch(() => navigate("/shop"));
  }, [id, navigate]);
  useEffect(() => {
    if (product?.pack_key) {
      api.get(`/pack/${product.pack_key}`).then(({ data }) => setPack(data.filter((p) => p.id !== product.id))).catch(() => setPack([]));
    } else { setPack([]); }
  }, [product]);
  useEffect(() => { if (user && product) setSaved((user.saved_items || []).includes(product.id)); }, [user, product]);
  useEffect(() => { if (user) api.get("/subscriptions/me").then(({ data }) => setIsVIP(data.active)).catch(() => {}); }, [user]);

  const toggleSave = async () => {
    if (!user) { navigate("/auth"); return; }
    try {
      const { data } = await api.post(`/account/saved/${product.id}`);
      const isSaved = data.saved_items.includes(product.id);
      setSaved(isSaved);
      toast.success(isSaved ? "Saved to your items" : "Removed from saved");
    } catch { toast.error("Could not update saved items"); }
  };

  if (!product) return <div className="h-[60vh] grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const isLux = product.category === "luxury";
  const eligible = !isLux;
  const subPrice = +(product.price * 0.85).toFixed(2);
  const colorways = product.colorways || {};
  const baseImg = (product.images || [])[activeImg];
  const displayImg = colorImg || baseImg;
  const activeColor = COLORS.find((c) => c.name === color);

  const pickColor = (name) => {
    setColor(name);
    if (colorways[name]) { setColorImg(colorways[name]); setTinted(false); }
    else { setColorImg(null); setTinted(true); }
  };

  const handleAdd = async () => {
    const chosen = { ...product, images: [displayImg] };
    if (sub && eligible) {
      addItem({ ...chosen, name: `${product.name} (Subscribe & Save)`, price: subPrice }, qty, { plan: "monthly", color });
      if (user) { try { await api.post("/subscriptions", { product_id: product.id }); setIsVIP(true); toast.success("Subscription active — VIP unlocked 🐾"); } catch { toast.error("Couldn't activate subscription"); } }
      else { toast("Added! Sign in to lock in your VIP subscription perks."); }
    } else {
      addItem(chosen, qty, { color });
    }
  };

  const addMatch = (m) => { addItem(m, 1, {}); toast.success(`${m.name} added — the pack matches now 🐾`); };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 overline text-muted-foreground hover:text-white mb-8" data-testid="pd-back"><ArrowLeft size={16} /> Back</button>
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-muted aspect-[4/5] group">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.img key={displayImg} src={displayImg} alt={product.name}
                initial={{ opacity: 0.3 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
                className="absolute inset-0 h-full w-full object-cover" data-testid="pd-main-image" />
            </AnimatePresence>
            {tinted && !colorImg && activeColor && (
              <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: activeColor.hex, mixBlendMode: "color", opacity: 0.55 }} data-testid="pd-tint" />
            )}
            <div className="absolute bottom-3 left-3 z-10 px-2.5 py-1.5 rounded-md bg-black/55 backdrop-blur-sm border border-white/10" data-testid="pd-mark">
              <WigglezMark size="text-sm" />
            </div>
            <button onClick={() => setZoom(true)} className="absolute bottom-3 right-3 h-11 w-11 grid place-items-center rounded-full bg-black/60 backdrop-blur border border-primary/40 text-primary hover:bg-black/80 transition-colors" data-testid="pd-zoom-btn" title="Zoom in on the details">
              <MagnifyingGlassPlus size={18} />
            </button>
          </div>
          {(product.images || []).length > 1 && (
            <div className="mt-3 flex gap-3" data-testid="pd-gallery">
              {product.images.map((im, i) => (
                <button key={i} onClick={() => { setActiveImg(i); setColorImg(null); setTinted(false); setColor(null); }} data-testid={`pd-thumb-${i}`}
                  className={`h-20 w-20 rounded-[10px] overflow-hidden border-2 transition-colors ${activeImg === i && !colorImg ? "border-primary" : "border-white/15 hover:border-white/40"}`}>
                  <img src={im} alt={`${product.name} ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="flex items-center gap-3 mb-3">
            <p className="overline text-primary">{isLux ? "Wigglez Luxury" : "Mr. Wigglez Shop"}</p>
            {isVIP && <span className="overline inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/15 text-secondary border border-secondary/40" data-testid="vip-badge"><Crown weight="fill" size={12} /> VIP Member</span>}
          </div>
          {product.slogan && <p className="text-sm sm:text-base font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2" data-testid="pd-slogan">{product.slogan}</p>}
          <h1 className={`tracking-tight mb-4 ${isLux ? "font-luxury font-light text-5xl gold-text" : "font-display font-black uppercase text-4xl sm:text-5xl"}`}>{product.name}</h1>
          <p className={`mb-6 ${isLux ? "gold-text font-luxury text-3xl" : "text-white text-2xl font-modern"}`}>
            ${(sub && eligible ? subPrice : product.price).toLocaleString()}
            {sub && eligible && <span className="ml-3 text-base text-muted-foreground line-through">${product.price.toLocaleString()}</span>}
          </p>
          {product.exclusivity && (
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-400/50 bg-amber-400/10 px-4 py-2" data-testid="pd-exclusivity">
              <Crown weight="fill" size={16} className="text-amber-400" />
              <span className="overline text-amber-400">{product.exclusivity} · Exclusive grail</span>
            </div>
          )}
          {product.signature && (
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-4 py-2" data-testid="pd-signature">
              <SealCheck weight="fill" size={16} className="text-accent" />
              <span className="overline text-accent">Authenticated · Signed — {product.signature}</span>
            </div>
          )}
          <p className="text-muted-foreground leading-relaxed mb-6">{product.description}</p>

          {eligible && (
            <div className="mb-6">
              <p className="overline text-muted-foreground mb-2 flex items-center gap-2 flex-wrap">
                Colorway
                {color
                  ? <span className="font-bold" style={{ color: (COLORS.find((c) => c.name === color) || {}).hex }} data-testid="pd-colorway-name">— {COLORWAY_LABEL[color] || color.toUpperCase()}</span>
                  : <span className="text-white/50">— pick one</span>}
                {Object.keys(colorways).length > 0 && <span className="text-secondary">· live photos</span>}
              </p>
              <div className="flex flex-wrap gap-2.5" data-testid="pd-swatches">
                {COLORS.map((c) => {
                  const hasPhoto = !!colorways[c.name];
                  const active = color === c.name;
                  return (
                    <button key={c.name} onClick={() => pickColor(c.name)} title={`${c.name}${hasPhoto ? " · live photo" : ""}`}
                      className="relative rounded-full border-2 transition-transform hover:scale-110"
                      style={{ height: 26, width: 26, background: c.hex, borderColor: active ? "#fff" : "rgba(255,255,255,0.25)", transform: active ? "scale(1.15)" : "scale(1)", boxShadow: active ? `0 0 9px ${c.hex}` : "none" }}
                      data-testid={`pd-swatch-${c.name.replace(/\s/g, "")}`} data-has-photo={hasPhoto ? "true" : "false"}>
                      {hasPhoto && <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-white ring-1 ring-black/40" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {eligible && (
            <div className="mb-8 rounded-xl border border-primary/25 bg-primary/5 p-4" data-testid="subscribe-save">
              <p className="overline text-primary mb-3 inline-flex items-center gap-1.5"><ArrowsClockwise weight="bold" size={13} /> How do you want it?</p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setSub(false)} data-testid="plan-onetime" className={`text-left rounded-[10px] border p-3 transition-colors ${!sub ? "border-primary bg-primary/10" : "border-white/15 hover:border-white/30"}`}>
                  <span className="block text-sm font-modern text-white">One-time</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">${product.price.toLocaleString()}</span>
                </button>
                <button onClick={() => setSub(true)} data-testid="plan-subscribe" className={`text-left rounded-[10px] border p-3 transition-colors ${sub ? "border-primary bg-primary/10" : "border-white/15 hover:border-white/30"}`}>
                  <span className="block text-sm font-modern text-white flex items-center gap-1.5">Subscribe & Save <span className="overline px-1.5 py-0.5 rounded bg-secondary/20 text-secondary">15% off</span></span>
                  <span className="block text-xs text-muted-foreground mt-0.5">${subPrice.toLocaleString()} / month</span>
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Ships monthly · cancel anytime · auto-joins Wigglez World drops. <span className="text-white/50">(auto-fulfillment simulated for now)</span></p>
            </div>
          )}

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-white/15 rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-2.5 text-lg hover:text-primary transition-colors" data-testid="pd-qty-dec">−</button>
              <span className="w-10 text-center" data-testid="pd-qty">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-4 py-2.5 text-lg hover:text-primary transition-colors" data-testid="pd-qty-inc">+</button>
            </div>
            {product.stock > 0 && product.stock < 10 ? (
              <span className="almost-gone inline-flex items-center gap-1 overline px-3 py-1.5 rounded-full bg-red-600 text-white" data-testid="pd-almost-gone">
                <Lightning weight="fill" size={12} /> Almost gone · only {product.stock} left
              </span>
            ) : (
              <span className="text-sm text-muted-foreground" data-testid="pd-stock">{product.stock > 0 ? `${product.stock} in stock` : "Sold out"}</span>
            )}
          </div>

          <div className="flex flex-wrap gap-4">
            <button onClick={handleAdd} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="pd-add-cart">
              {sub && eligible ? <ArrowsClockwise weight="fill" size={18} /> : <ShoppingBag weight="fill" size={18} />}
              {sub && eligible ? `Subscribe · $${subPrice}/mo` : "Add to Bag"}
            </button>
            <button onClick={toggleSave} className={`inline-flex items-center gap-2 px-6 py-3.5 rounded-full border font-modern transition-colors ${saved ? "border-accent text-accent" : "border-white/20 text-white hover:border-white/40"}`} data-testid="pd-save">
              <Heart weight={saved ? "fill" : "regular"} size={18} /> {saved ? "Saved" : "Save"}
            </button>
          </div>

          {pack.length > 0 && (
            <div className="mt-8 rounded-xl border border-secondary/25 bg-secondary/5 p-5" data-testid="complete-the-pack">
              <p className="overline text-secondary mb-1 inline-flex items-center gap-1.5"><PawPrint weight="fill" size={13} /> Complete the Pack</p>
              <p className="text-sm text-muted-foreground mb-4">Your dog matches. One tap adds the matching piece from {product.pack_label || "this pack"} — cut to be worn together.</p>
              <div className="space-y-3">
                {pack.map((m) => (
                  <div key={m.id} className="flex items-center gap-4 rounded-xl border border-white/10 bg-black/30 p-3" data-testid={`pack-item-${m.id}`}>
                    <Link to={`/product/${m.id}`}><img src={(m.images || [])[0]} alt={m.name} className="h-16 w-16 rounded-lg object-cover" /></Link>
                    <div className="min-w-0 flex-1">
                      <p className="overline text-muted-foreground text-[10px]">{m.pack_role === "dog" ? "Matching dog piece" : "Matching human fit"}</p>
                      <p className="font-display font-black uppercase text-lg leading-tight truncate">{m.name}</p>
                      <p className="text-sm text-muted-foreground">${m.price.toLocaleString()}</p>
                    </div>
                    <button onClick={() => addMatch(m)} className="shrink-0 inline-flex items-center gap-2 bg-secondary text-black px-4 py-2.5 rounded-[10px] font-modern hover:opacity-90 transition-opacity" data-testid={`pack-add-${m.id}`}>
                      <PawPrint weight="fill" size={15} /> Add the match
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isLux && (
            <div className="mt-10 p-5 rounded-xl border border-secondary/20 bg-secondary/5">
              <p className="overline text-secondary mb-1">Concierge service</p>
              <p className="text-sm text-muted-foreground">Interested in a bespoke Luxury piece? <Link to="/contact" className="text-secondary hover:underline">Submit a luxury request →</Link></p>
            </div>
          )}
        </div>
      </div>

      {/* Zoom modal */}
      <AnimatePresence>
        {zoom && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/90 backdrop-blur grid place-items-center p-4" onClick={() => setZoom(false)} data-testid="pd-zoom-modal">
            <button className="absolute top-5 right-5 h-11 w-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20" data-testid="pd-zoom-close"><X size={20} /></button>
            <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={displayImg} alt={product.name} className="w-full max-h-[80vh] object-contain rounded-xl" />
              <p className="text-center overline text-primary mt-4">Zoom in on the neon-camo hood lining & double-stitched detail{color ? ` · ${color}` : ""}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
